import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, take, timeout } from 'rxjs/operators';

import { PLATFORM_CHAT_ENTITY_TYPE } from '../components/platform-comments-chat/platform-chat-entity.const';
import {
  ActiveSubsectionState,
  OptionInfo,
  ProjectSection,
  ProjectSubsection,
  QueryState,
  TopicInfo,
} from '../interfaces';
import { AuthService } from './auth.service';
import { ProjectService } from './project.service';
import { SubsectionService } from './subsection.service';
import { TimelineService } from './timeline.service';

const UUID_RE_SOURCE = '([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})';

// Drive folders/sub-folders aren't stored in legacy subsection state, so on a cold load their
// name never arrives. Bound the wait so the signature request still fires with an empty name.
const FOLDER_NAME_TIMEOUT_MS: number = 100;

@Injectable({ providedIn: 'root' })
export class EntityItemNameResolverService {
  private readonly auth: AuthService = inject(AuthService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly subsectionService: SubsectionService = inject(SubsectionService);
  private readonly timelineService: TimelineService = inject(TimelineService);
  private readonly router: Router = inject(Router);

  /**
   * Emits exactly once when the entity name becomes available, then completes.
   * Caller should treat this as "wait until ready" — the signature request /
   * component init should only fire on this emission. No artificial timeout:
   * if the name never arrives the caller's subscription is cleaned up on destroy.
   */
  public resolve(entityType: string | null, entityUuid: string | null, fallback: string = ''): Observable<string> {
    if (!entityType || !entityUuid) {
      return of(fallback || this.auth.getUserBrand()?.name || '');
    }

    const fromState: string = this.resolveFromState(entityType, entityUuid);
    if (fromState) {
      return of(fromState);
    }

    if (fallback) {
      return of(fallback);
    }

    return this.waitForName(entityType, entityUuid);
  }

  private resolveFromState(entityType: string, entityUuid: string): string {
    const brandName: string = this.auth.getUserBrand()?.name ?? '';
    const subState: ActiveSubsectionState = this.subsectionService.subsection$.getValue();

    switch (entityType) {
      case PLATFORM_CHAT_ENTITY_TYPE.BRAND:
        return brandName;
      case PLATFORM_CHAT_ENTITY_TYPE.PROJECT:
        return this.projectService.project?.name ?? '';
      case PLATFORM_CHAT_ENTITY_TYPE.SECTION:
        return this.resolveSectionName(entityUuid) || subState?.section?.name || '';
      case PLATFORM_CHAT_ENTITY_TYPE.SUB_SECTION:
        return this.resolveSubsectionName(entityUuid) || subState?.subsection?.name || '';
      case PLATFORM_CHAT_ENTITY_TYPE.FOLDER:
        return subState?.activeTopic?.topicId === entityUuid ? (subState.activeTopic.title ?? '') : '';
      case PLATFORM_CHAT_ENTITY_TYPE.SUB_FOLDER:
        return subState?.activeOption?.optionId === entityUuid ? (subState.activeOption.title ?? '') : '';
      case PLATFORM_CHAT_ENTITY_TYPE.VERSION:
        return subState?.versionData?.versionUuid === entityUuid
          ? (subState.versionData.versionName ?? '')
          : (subState?.activeVersion?.name ?? '');
      case PLATFORM_CHAT_ENTITY_TYPE.FILE:
        return subState?.activeFile?.id === entityUuid
          ? (subState.activeFile.displayName ?? subState.activeFile.name ?? '')
          : '';
      default:
        return '';
    }
  }

  private waitForName(entityType: string, entityUuid: string): Observable<string> {
    switch (entityType) {
      case PLATFORM_CHAT_ENTITY_TYPE.WORKSPACE:
        return this.timelineService.workspaceName$.pipe(
          filter((name: string) => !!name),
          take(1),
          timeout(location.pathname.startsWith('/workspaces/campaign-drive') ? 1000 : 100),
          catchError(() => of(''))
        );

      case PLATFORM_CHAT_ENTITY_TYPE.CAMPAIGN:
        return this.timelineService.activeCampaign$.pipe(
          filter((c) => !!c?.name),
          map((c) => c?.name ?? ''),
          take(1)
        );

      case PLATFORM_CHAT_ENTITY_TYPE.FOLDER:
        return this.subsectionService.topics$.pipe(
          map((d: QueryState<TopicInfo>) => d.data.find((t: TopicInfo) => t.topicId === entityUuid)?.title ?? ''),
          filter((name: string) => !!name),
          take(1),
          timeout(FOLDER_NAME_TIMEOUT_MS),
          catchError(() => of(''))
        );

      case PLATFORM_CHAT_ENTITY_TYPE.SUB_FOLDER:
        return this.subsectionService.options$.pipe(
          map((d: QueryState<OptionInfo>) => d.data.find((o: OptionInfo) => o.optionId === entityUuid)?.title ?? ''),
          filter((name: string) => !!name),
          take(1),
          timeout(FOLDER_NAME_TIMEOUT_MS),
          catchError(() => of(''))
        );

      default:
        return of(this.auth.getUserBrand()?.name ?? '');
    }
  }

  private resolveSectionName(sectionUuid: string): string {
    const { project } = this.projectService;
    if (!project?.sections) return '';
    return project.sections.find((s: ProjectSection) => s.id === sectionUuid)?.name ?? '';
  }

  private resolveSubsectionName(subsectionUuid: string): string {
    const { project } = this.projectService;
    if (!project?.sections) return '';
    const reSubsection = new RegExp(`${UUID_RE_SOURCE}/${UUID_RE_SOURCE}`);
    const match = this.router.url.match(reSubsection);
    const sectionId: string | undefined = match?.[1];
    const sections: ProjectSection[] = sectionId
      ? project.sections.filter((s: ProjectSection) => s.id === sectionId)
      : project.sections;
    for (const section of sections) {
      const sub: ProjectSubsection | undefined = section?.projectSubSections?.find(
        (ss: ProjectSubsection) => ss.id === subsectionUuid
      );
      if (sub) return sub.name ?? '';
    }
    return '';
  }
}
